import { useMemo, useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Users } from 'lucide-react';

/**
 * Phase 5-C.3 + Wireframe-v1 — Remedial Assessment generation dialog.
 *
 * Props:
 *   sourceAid           — parent assessment id
 *   sourceTitle         — parent title (display only)
 *   conceptTag          — LEGACY single-topic prop (kept for back-compat;
 *                         wrapped into a single-item array internally)
 *   conceptTags         — NEW: array of 1-5 topics for multi-topic remedial
 *   defaultDurationMinutes
 *   targetEnrollmentIds — NEW: array of enrollment_ids to scope audience.
 *                         null/undefined = whole class (legacy).
 *   targetStudentLabel  — Optional human label for audience summary
 *                         (e.g. "Suman Saurabh only"). When omitted, a default
 *                         is derived from the array length.
 *   onCreated           — fn(newAssessmentId) callback after success
 */
export default function RemedialAssessmentDialog({
  open,
  onOpenChange,
  sourceAid,
  sourceTitle,
  conceptTag,
  conceptTags,
  defaultDurationMinutes = 20,
  targetEnrollmentIds = null,
  targetStudentLabel = null,
  onCreated,
}) {
  // Normalise topic input — prefer array, fall back to legacy single tag.
  const tags = useMemo(() => {
    if (Array.isArray(conceptTags) && conceptTags.length) {
      return conceptTags.map((t) => (t || '').trim()).filter(Boolean);
    }
    if (conceptTag && typeof conceptTag === 'string' && conceptTag.trim()) {
      return [conceptTag.trim()];
    }
    return [];
  }, [conceptTags, conceptTag]);

  // Question budget — must be at least 2 per topic.
  const minQuestions = Math.max(3, tags.length * 2);
  const initialQuestions = Math.max(minQuestions, 5);

  const [numQuestions, setNumQuestions] = useState(initialQuestions);
  const [dueAtLocal, setDueAtLocal] = useState(() => {
    const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [durationMinutes, setDurationMinutes] = useState(defaultDurationMinutes || 20);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync numQuestions when the topic list changes (e.g. dialog reopened
  // with a different topic count).
  useEffect(() => {
    if (open) {
      setNumQuestions((prev) => Math.max(prev, minQuestions, 5));
      setError(null);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tags.length]);

  // Helper: YYYY-MM-DD -> ISO at end-of-day (23:59 local)
  const dateOnlyToEodIso = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(`${dateStr}T23:59:00`);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const dueAtInPast = useMemo(() => {
    const iso = dateOnlyToEodIso(dueAtLocal);
    if (!iso) return false;
    return new Date(iso).getTime() <= Date.now();
  }, [dueAtLocal]);

  // Audience UI summary.
  const audienceSummary = useMemo(() => {
    if (!Array.isArray(targetEnrollmentIds) || targetEnrollmentIds.length === 0) {
      return 'Whole class';
    }
    if (targetStudentLabel) return targetStudentLabel;
    const n = targetEnrollmentIds.length;
    return `${n} student${n === 1 ? '' : 's'} only`;
  }, [targetEnrollmentIds, targetStudentLabel]);

  const handleSubmit = async () => {
    setError(null);
    if (tags.length === 0) {
      setError('Topic is missing.');
      return;
    }
    if (!Number.isInteger(numQuestions) || numQuestions < minQuestions || numQuestions > 25) {
      setError(`Number of questions must be between ${minQuestions} and 25.`);
      return;
    }
    if (dueAtInPast) {
      setError('Due date must be in the future.');
      return;
    }
    if (!Number.isInteger(durationMinutes) || durationMinutes < 5 || durationMinutes > 180) {
      setError('Duration must be between 5 and 180 minutes.');
      return;
    }
    setSubmitting(true);
    try {
      const dueAtIso = dateOnlyToEodIso(dueAtLocal);
      const payload = {
        concept_tags: tags,
        num_questions: numQuestions,
        due_at: dueAtIso,
        duration_minutes: durationMinutes,
      };
      if (Array.isArray(targetEnrollmentIds) && targetEnrollmentIds.length > 0) {
        payload.assigned_to_enrollment_ids = targetEnrollmentIds;
      }
      const { data } = await api.post(
        `/teacher/me/assessments/${sourceAid}/remedial`,
        payload,
      );
      const newAid = data?.assessment?.assessment_id;
      if (data?.generation_warning) {
        toast.warning(data.generation_warning);
      } else {
        toast.success('Remedial draft created — review the questions before publishing.');
      }
      onOpenChange?.(false);
      if (newAid && typeof onCreated === 'function') onCreated(newAid);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to generate remedial assessment.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange?.(next); }}>
      <DialogContent className="sm:max-w-md" data-testid="remedial-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500" />
            Generate Remedial Assessment
          </DialogTitle>
          <DialogDescription>
            {tags.length > 1
              ? `A focused draft scoped to ${tags.length} sub-topics. You'll review and edit the questions before publishing.`
              : "A focused draft scoped to one sub-topic. You'll review and edit the questions before publishing."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Read-only context */}
          <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-2">
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-500">
                {tags.length > 1 ? 'Topics' : 'Topic'}
              </span>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="remedial-topic-display">
                {tags.map((t) => (
                  <Badge
                    key={t}
                    variant="outline"
                    className="bg-amber-50 text-amber-800 border-amber-300"
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-500">Source</span>
              <div className="text-slate-700">{sourceTitle}</div>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-500">Audience</span>
              <div
                className="flex items-center gap-1.5 text-slate-700"
                data-testid="remedial-audience-display"
              >
                <Users size={13} className="text-slate-500" />
                <span>{audienceSummary}</span>
              </div>
            </div>
          </div>

          {/* Number of questions */}
          <div className="space-y-1.5">
            <Label htmlFor="remedial-num-q">Number of questions</Label>
            <div className="flex items-center gap-2">
              {[Math.max(minQuestions, 3), Math.max(minQuestions, 5), Math.max(minQuestions, 10)].map((n, i, arr) => {
                // Dedupe quick buttons
                if (arr.indexOf(n) !== i) return null;
                return (
                  <Button
                    key={n}
                    type="button"
                    variant={numQuestions === n ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNumQuestions(n)}
                    data-testid={`remedial-num-quick-${n}`}
                  >
                    {n}
                  </Button>
                );
              })}
              <Input
                id="remedial-num-q"
                type="number"
                min={minQuestions}
                max={25}
                value={numQuestions}
                onChange={(e) => setNumQuestions(Number(e.target.value) || 0)}
                className="w-20 h-9"
                data-testid="remedial-num-input"
              />
              <span className="text-xs text-slate-500">{minQuestions} – 25</span>
            </div>
            {tags.length > 1 && (
              <p className="text-[11px] text-slate-500">
                Questions are split evenly across {tags.length} topics.
              </p>
            )}
          </div>

          {/* Due date */}
          <div className="space-y-1.5">
            <Label htmlFor="remedial-due">Due date</Label>
            <Input
              id="remedial-due"
              type="date"
              value={dueAtLocal}
              onChange={(e) => setDueAtLocal(e.target.value)}
              className="h-9"
              data-testid="remedial-due-input"
            />
            {dueAtInPast && (
              <p className="text-xs text-rose-600">Due date must be in the future.</p>
            )}
          </div>

          {/* Duration */}
          <div className="space-y-1.5">
            <Label htmlFor="remedial-duration">Duration (minutes)</Label>
            <Input
              id="remedial-duration"
              type="number"
              min={5}
              max={180}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Number(e.target.value) || 0)}
              className="w-28 h-9"
              data-testid="remedial-duration-input"
            />
          </div>

          {error && (
            <p className="text-sm text-rose-600" data-testid="remedial-error">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { setError(null); onOpenChange?.(false); }}
            disabled={submitting}
            data-testid="remedial-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || dueAtInPast || tags.length === 0}
            data-testid="remedial-submit"
          >
            {submitting
              ? (<><Loader2 size={14} className="animate-spin mr-1" /> Generating…</>)
              : 'Generate draft →'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
